import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ExecutionQueue.css';
import RelativeTime from './RelativeTime';
import DurationTimer from './DurationTimer';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import RawEntryFields from './RawEntryFields';
import { startActivityAwarePolling } from '../utils/activityAwarePolling';

// ExecutionQueue component — integrates with Prompt Executions API
// Props:
// - promptIds: string[] — only show queue items for these prompt ids (actionable on the page)
// - promptNameById: Record<string,string> — optional mapping for display names
// - pageKey: string — used to persist collapsed state per page
// - includeCompleted?: boolean — include finished/canceled items (default false)
// - pollIntervalMs?: number — polling frequency (default 5000)
// - onRequestComplete?: (id:string) => void — optional callback when an item completes (no-op by default)
// - horizonSuffix?: string — ingestion/editor horizon; used when loading entries for a request

// Use shared API_BASE_URL from utils (defaults to current origin if env not set)

const STATUS_COLORS = {
  // Execution request statuses
  created: '#f59e0b', // yellow
  processing: '#2563eb', // blue
  canceled: '#dc2626', // red
  finished: '#16a34a', // green
  errored: '#dc2626', // red (updated per spec)
  // Entry-level statuses for progress bar
  processed: '#16a34a', // green (added per spec)
  // Unprocessed (remaining) items — ensure we have an explicit color mapping
  unprocessed: '#9ca3af', // gray
  unknown: '#9ca3af', // gray
};

function SegmentedProgress({ statusCounts }) {
  const total = Object.values(statusCounts).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0);
  if (!total) return null;

  // Normalize incoming status keys to our canonical set used for the progress bar
  const normalize = (k) => {
    switch (String(k || '').toLowerCase()) {
      case 'finished':
      case 'done':
        return 'processed';
      case 'processed':
        return 'processed';
      case 'errored':
        return 'errored';
      case 'processing':
        return 'processing';
      case 'pending':
      case 'queued':
      case 'created':
      case 'unprocessed':
        return 'unprocessed';
      default:
        return String(k || 'unknown');
    }
  };

  // Aggregate counts per normalized key and drop non-positive values
  const aggregated = Object.entries(statusCounts).reduce((acc, [k, v]) => {
    const num = typeof v === 'number' ? v : 0;
    if (num <= 0) return acc;
    const key = normalize(k);
    acc[key] = (acc[key] || 0) + num;
    return acc;
  }, {});

  // Desired left-to-right order per spec
  const desiredOrder = ['processed', 'errored', 'processing', 'unprocessed'];
  const orderIndex = (k) => {
    const idx = desiredOrder.indexOf(k);
    return idx === -1 ? desiredOrder.length + 1 : idx;
  };

  const orderedKeys = Object.keys(aggregated).sort((a, b) => orderIndex(a) - orderIndex(b));
  const segments = orderedKeys.map((status) => ({ status, count: aggregated[status], pct: (aggregated[status] / total) * 100 }));

  // Compose a single tooltip for the whole progress bar showing all statuses in the same order
  const tooltipLines = [
    `Total: ${total}`,
    ...segments.map(s => `${s.status}: ${s.count} (${Math.round((s.count / total) * 100)}%)`),
  ];
  const combinedTitle = tooltipLines.join('\n');

  return (
    <div className="eq-progress">
      <div className="eq-progress-bar" title={combinedTitle} aria-label={combinedTitle}>
        {segments.map(seg => (
          <div
            key={seg.status}
            className="eq-progress-segment"
            style={{ width: `${seg.pct}%`, background: STATUS_COLORS[seg.status] || STATUS_COLORS.unknown }}
            title={combinedTitle}
          />
        ))}
      </div>
      <div className="eq-progress-total">Total: {total}</div>
    </div>
  );
}

function metadataLooksLikeStatsOnly(value) {
  if (value == null) return false;
  if (typeof value === 'string') {
    const t = value.trim();
    if (!t.startsWith('{')) return false;
    try {
      const parsed = JSON.parse(t);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
      const keys = Object.keys(parsed);
      return keys.length === 1 && keys[0] === 'stats';
    } catch {
      return t.includes('"stats"') && !t.includes('"error"');
    }
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const keys = Object.keys(value).filter(k => value[k] != null);
    return keys.length === 1 && keys[0] === 'stats';
  }
  return false;
}

function extractExplicitError(metadata) {
  if (!metadata) return null;
  if (typeof metadata === 'string') {
    if (metadataLooksLikeStatsOnly(metadata)) return null;
    return metadata;
  }
  if (typeof metadata !== 'object') return null;
  if (metadataLooksLikeStatsOnly(metadata)) return null;
  if (typeof metadata.error === 'string' && metadata.error.trim()) {
    if (metadataLooksLikeStatsOnly(metadata.error)) return null;
    return metadata.error.trim();
  }
  if (metadata.debug && typeof metadata.debug === 'object') {
    if (typeof metadata.debug.error === 'string' && metadata.debug.error.trim()) {
      return metadata.debug.error.trim();
    }
    const fkMsg = metadata.debug.fk_error?.message;
    if (typeof fkMsg === 'string' && fkMsg.trim()) return fkMsg.trim();
  }
  if (typeof metadata.message === 'string' && metadata.message.trim()) return metadata.message.trim();
  return null;
}

function formatPromotionStats(metadata) {
  const stats = metadata?.stats;
  if (!stats || typeof stats !== 'object') return null;
  const inserted = Number(stats.inserted);
  const total = Number(stats.totalMembership ?? stats.attemptedInserts);
  const skipped = Number(stats.skipped || 0);
  if (Number.isNaN(inserted) && Number.isNaN(total)) return null;
  let line = `Prepared ${Number.isNaN(inserted) ? '?' : inserted} of ${Number.isNaN(total) ? '?' : total} entries for processing`;
  if (skipped > 0) line += ` (${skipped} skipped due to missing entry references)`;
  return line;
}

/** True when the run actually failed — not when metadata only has promotion stats. */
function requestHasFailure(item) {
  if (!item) return false;
  if (item.status === 'errored') return true;
  if (extractExplicitError(item.metadata)) return true;
  const sc = item.entries?.status_counts;
  if (sc && typeof sc === 'object' && Number(sc.errored || 0) > 0) return true;
  return false;
}

function StatusBadge({ status, failed }) {
  const displayStatus = failed && status === 'finished' ? 'finished (errors)' : (status || 'unknown');
  const colorKey = failed && status === 'finished' ? 'errored' : status;
  const color = STATUS_COLORS[colorKey] || STATUS_COLORS.unknown;
  const styleVar = { '--status-color': color };
  return (
    <span className="eq-status-badge" style={styleVar}>
      <span className="eq-status-dot" />
      {displayStatus}
    </span>
  );
}

function resolveHorizonSuffixForItem(item, horizonSuffix) {
  const fromMeta = item?.metadata?.horizon_suffix;
  if (fromMeta != null && String(fromMeta).trim()) return String(fromMeta).trim();
  if (horizonSuffix != null && String(horizonSuffix).trim()) return String(horizonSuffix).trim();
  return null;
}

function formatExecutionReport(report) {
  if (!report || typeof report !== 'object') return null;
  const entries = Array.isArray(report.entries) ? report.entries : [];
  if (!entries.length) return null;
  return entries;
}

function ExecutionQueue({
  promptIds = [],
  promptNameById = {},
  pageKey = 'default',
  includeCompleted = false,
  showCompletedRuns = false,
  pollIntervalMs = 5000,
  onRequestComplete = () => {},
  fileDownloadContext = null,
  horizonSuffix = null,
}) {
  // tick state used to update ETA/elapsed timers smoothly between polls
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(`executionQueue.collapsed.${pageKey}`);
      return raw === 'true';
    } catch {
      return false;
    }
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Per-request entries state (pagination + data + errors)
  const [entriesStateByRequestId, setEntriesStateByRequestId] = useState({});
  // Track which request rows are expanded to show entries
  const [expandedRequestIds, setExpandedRequestIds] = useState(new Set());
  // Track which items are being dismissed to avoid duplicate requests and show spinners
  const [dismissingIds, setDismissingIds] = useState(new Set());
  // Track which items are in the process of being canceled
  const [cancelingIds, setCancelingIds] = useState(new Set());
  // Keep a ref of items to avoid stale closures inside polling
  const itemsRef = useRef([]);
  // Expose fetchQueue in a ref so event listeners can call latest version
  const fetchRef = useRef(null);

  const hasFilters = Array.isArray(promptIds) && promptIds.length > 0;

  // Lightweight ticker that runs when there is at least one processing item to keep ETA fresh
  useEffect(() => {
    const hasProcessing = Array.isArray(itemsRef.current) && itemsRef.current.some(x => x && x.status === 'processing');
    if (!hasProcessing) return; // avoid unnecessary intervals
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [items]);

  const fetchQueue = async () => {
    try {
      setError(null);
      setLoading(items.length === 0); // only show spinner if empty
      const url = new URL(`${API_BASE_URL}/casc/v1/api/prompt-executions`);
      // Always request completed items from the API so that canceled items are included.
      // We'll locally filter out "finished" items if includeCompleted is false, but keep canceled.
      url.searchParams.set('includeCompleted', 'true');
      url.searchParams.set('page', '1');
      url.searchParams.set('pageSize', '20');
      const headers = await getAuthHeaders();
      const resp = await fetch(url.toString(), { method: 'GET', headers });
      if (!resp.ok) {
        if (resp.status === 400) throw new Error('Invalid pagination or query parameters.');
        if (resp.status === 503) throw new Error('Service temporarily unavailable.');
        throw new Error(`Request failed (${resp.status}).`);
      }
      const data = await resp.json(); // expected array
      let arr = Array.isArray(data) ? data : [];
      if (hasFilters) {
        const setIds = new Set(promptIds.map(String));
        arr = arr.filter(x => setIds.has(String(x.prompt_id)));
      }
      // Hide successful finished runs unless includeCompleted or showCompletedRuns (ingestion page)
      if (!includeCompleted && !showCompletedRuns) {
        arr = arr.filter(x => {
          if (!x || x.status !== 'finished') return true;
          return requestHasFailure(x);
        });
      }
      // Do not show acknowledged errored/canceled/failed-finished items
      const filtered = arr.filter(x => {
        if (!x) return false;
        const acknowledged = x.metadata && x.metadata.acknowledged === true;
        if (acknowledged && (x.status === 'errored' || x.status === 'canceled' || x.status === 'finished')) {
          return false;
        }
        return true;
      });
      setItems(filtered);
      itemsRef.current = arr;
      // Fire completion callback for any items that are done
      arr.forEach(x => {
        if (x && (x.status === 'finished' || x.status === 'canceled')) {
          onRequestComplete && onRequestComplete(x.id);
        }
      });
    } catch (e) {
      setError(e.message || 'Failed to load queue.');
    } finally {
      setLoading(false);
    }
  };

  // Helpers to manage per-request entries state
  const getDefaultEntriesState = () => ({
    items: [],
    page: 1,
    pageSize: 20,
    loading: false,
    error: null,
    hasMore: true,
    // enrichment with entry data
    entryDataById: {},
    entryDataLoading: false,
    entryDataError: null,
    expandedEntryIds: new Set(),
  });

  const toggleExpand = async (requestId) => {
    setExpandedRequestIds(prev => {
      const next = new Set(prev);
      if (next.has(requestId)) next.delete(requestId); else next.add(requestId);
      return next;
    });
    // Lazy-load entries when expanding first time
    setEntriesStateByRequestId(prev => {
      if (prev[requestId]) return prev; // already initialized, loader will decide
      return { ...prev, [requestId]: getDefaultEntriesState() };
    });
    // Kick off load for first page if not present
    await loadEntriesPage(requestId, 1, 20, true);
  };

  const loadEntriesPage = async (requestId, page = 1, pageSize = 20, onlyIfEmpty = false) => {
    setEntriesStateByRequestId(prev => {
      const cur = prev[requestId] || getDefaultEntriesState();
      if (onlyIfEmpty && cur.items && cur.items.length > 0) return prev;
      return { ...prev, [requestId]: { ...cur, loading: true, error: null } };
    });
    try {
      const url = new URL(`${API_BASE_URL}/casc/v1/api/prompt-executions/${requestId}/entries`);
      url.searchParams.set('page', String(page || 1));
      url.searchParams.set('pageSize', String(pageSize || 50));
      // Load all statuses so operators can debug processed vs errored rows
      url.searchParams.set('status', 'unprocessed,processing,errored,processed');
      const item = items.find(x => x.id === requestId);
      const sfx = resolveHorizonSuffixForItem(item, horizonSuffix);
      if (sfx) url.searchParams.set('suffix', sfx);
      const headers = await getAuthHeaders();
      const resp = await fetch(url.toString(), { method: 'GET', headers });
      if (!resp.ok) {
        let msg = `Failed to load entries (${resp.status}).`;
        if (resp.status === 400) msg = 'Invalid pagination parameters, status filter, or query.';
        if (resp.status === 503) msg = 'Service temporarily unavailable.';
        throw new Error(msg);
      }
      const data = await resp.json();
      const arr = Array.isArray(data) ? data : [];
      const hasMore = arr.length >= (pageSize || 20);
      setEntriesStateByRequestId(prev => {
        const cur = prev[requestId] || getDefaultEntriesState();
        // If requesting page 1 anew, replace; else append
        const newItems = page <= 1 ? arr : [...(cur.items || []), ...arr];
        return { ...prev, [requestId]: { ...cur, items: newItems, page, pageSize, loading: false, error: null, hasMore } };
      });

      // After we have the page items, fetch the actual entry data for display
      const entryIds = arr.map(en => en && en.entry_id).filter(Boolean);
      if (entryIds.length > 0) {
        await loadEntryDataFor(requestId, entryIds);
      } else {
        // clear previous data errors/loading if page empty
        setEntriesStateByRequestId(prev => {
          const cur = prev[requestId] || getDefaultEntriesState();
          return { ...prev, [requestId]: { ...cur, entryDataLoading: false, entryDataError: null } };
        });
      }
    } catch (e) {
      setEntriesStateByRequestId(prev => {
        const cur = prev[requestId] || getDefaultEntriesState();
        return { ...prev, [requestId]: { ...cur, loading: false, error: e.message || String(e) } };
      });
    }
  };

  // Fetch entry data for given entry ids and store by id
  const loadEntryDataFor = async (requestId, entryIds) => {
    setEntriesStateByRequestId(prev => {
      const cur = prev[requestId] || getDefaultEntriesState();
      return { ...prev, [requestId]: { ...cur, entryDataLoading: true, entryDataError: null } };
    });
    try {
      // Respect API limit (<=200); our pages are <=20 so safe
      const url = new URL(`${API_BASE_URL}/casc/v1/api/data-management/entries`);
      // Use comma-separated list to preserve order, server also preserves order
      url.searchParams.set('ids', entryIds.join(','));
      const headers = await getAuthHeaders();
      const resp = await fetch(url.toString(), { method: 'GET', headers });
      if (!resp.ok) {
        let msg = `Failed to load entry data (${resp.status}).`;
        if (resp.status === 400) msg = 'Invalid entry ids provided.';
        if (resp.status === 503) msg = 'Service temporarily unavailable.';
        throw new Error(msg);
      }
      const payload = await resp.json();
      const entriesArr = payload && Array.isArray(payload.entries) ? payload.entries : [];
      const map = {};
      // The response preserves requested order and omits not-found ids
      for (const row of entriesArr) {
        if (row && (row.id || row.entry_id)) {
          const key = String(row.id || row.entry_id);
          map[key] = row;
        }
      }
      setEntriesStateByRequestId(prev => {
        const cur = prev[requestId] || getDefaultEntriesState();
        const merged = { ...(cur.entryDataById || {}), ...map };
        return { ...prev, [requestId]: { ...cur, entryDataById: merged, entryDataLoading: false, entryDataError: null } };
      });
    } catch (e) {
      setEntriesStateByRequestId(prev => {
        const cur = prev[requestId] || getDefaultEntriesState();
        return { ...prev, [requestId]: { ...cur, entryDataLoading: false, entryDataError: e.message || String(e) } };
      });
    }
  };

  const toggleEntryDetails = (requestId, entryId) => {
    setEntriesStateByRequestId(prev => {
      const cur = prev[requestId] || getDefaultEntriesState();
      const nextSet = new Set(cur.expandedEntryIds || []);
      const key = String(entryId);
      if (nextSet.has(key)) nextSet.delete(key); else nextSet.add(key);
      return { ...prev, [requestId]: { ...cur, expandedEntryIds: nextSet } };
    });
  };

  // Keep latest fetch function in a ref for external triggers
  useEffect(() => {
    fetchRef.current = fetchQueue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeCompleted, hasFilters, pollIntervalMs, JSON.stringify(promptIds)]);

  // Keep itemsRef in sync when items state changes
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Polling (slows after user inactivity)
  useEffect(() => {
    const stopPolling = startActivityAwarePolling(
      () => {
        if (document.visibilityState !== 'visible') return;
        // Only poll the backend when there are active items we care about
        // Active = created or processing. If only errored/completed items are present, skip polling.
        const hasActive = Array.isArray(itemsRef.current)
          && itemsRef.current.some(x => x && (
            x.status === 'created' ||
            x.status === 'processing' ||
            (requestHasFailure(x) && !(x.metadata && x.metadata.acknowledged === true))
          ));
        if (hasActive) {
          return fetchQueue();
        }
      },
      Math.max(2000, pollIntervalMs),
      { immediate: true }
    );
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeCompleted, pollIntervalMs, hasFilters, JSON.stringify(promptIds)]);

  // Listen for execution request creations to immediately refresh the queue
  useEffect(() => {
    const handler = (evt) => {
      try {
        const detail = evt && evt.detail ? evt.detail : {};
        // If filters are applied, only refresh when the created prompt_id is relevant
        if (hasFilters) {
          const setIds = new Set(promptIds.map(String));
          if (!setIds.has(String(detail.prompt_id))) {
            return; // not relevant to this queue view
          }
        }
        if (typeof fetchRef.current === 'function') {
          fetchRef.current();
        }
      } catch {
        // no-op
      }
    };
    window.addEventListener('executionRequestCreated', handler);
    return () => window.removeEventListener('executionRequestCreated', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFilters, JSON.stringify(promptIds)]);

  useEffect(() => {
    try {
      localStorage.setItem(`executionQueue.collapsed.${pageKey}`, collapsed ? 'true' : 'false');
    } catch {}
  }, [collapsed, pageKey]);

  // Only items visible in the list (exclude cleared / acknowledged items)
  const visibleItems = useMemo(
    () => items.filter(x => {
      if (!x) return false;
      const acknowledged = x.metadata && x.metadata.acknowledged === true;
      if (acknowledged) return false;
      return true;
    }),
    [items]
  );

  const activeCount = useMemo(
    () => visibleItems.filter(x => {
      if (!x) return false;
      if (x.status === 'created' || x.status === 'processing') return true;
      if (requestHasFailure(x) && !(x.metadata && x.metadata.acknowledged === true)) return true;
      return false;
    }).length,
    [visibleItems]
  );

  const failedCount = useMemo(
    () => visibleItems.filter(x => requestHasFailure(x)).length,
    [visibleItems]
  );

  // Helpers for ETA calculation
  const normalizeForProgress = (k) => {
    switch (String(k || '').toLowerCase()) {
      case 'finished':
      case 'done':
      case 'processed':
        return 'processed';
      case 'errored':
        return 'errored';
      case 'processing':
        return 'processing';
      case 'pending':
      case 'queued':
      case 'created':
      case 'unprocessed':
        return 'unprocessed';
      default:
        return String(k || 'unknown');
    }
  };

  const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
  const formatDuration = (ms) => {
    if (!ms || ms < 0) ms = 0;
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) return `${h}:${pad2(m)}:${pad2(s)}`;
    return `${m}:${pad2(s)}`;
  };

  // Remove a run from the queue (sets metadata.acknowledged on the server)
  const clearItemFromQueue = async (id) => {
    try {
      setDismissingIds(prev => new Set(prev).add(id));
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/casc/v1/api/prompt-executions/${id}/acknowledge`, {
        method: 'POST',
        headers,
        body: '{}',
      });
      if (!resp.ok) {
        if (resp.status === 404) throw new Error('Request not found.');
        if (resp.status === 400) throw new Error('Invalid request id.');
        if (resp.status === 503) throw new Error('Service temporarily unavailable.');
        throw new Error(`Acknowledge failed (${resp.status}).`);
      }
      // Optimistically hide the item right away
      setItems(prev => prev.filter(x => x && x.id !== id));
    } catch (e) {
      console.error('[ExecutionQueue] Failed to clear item', id, e);
      try { window.alert(`Failed to clear: ${e.message || e}`); } catch {}
    } finally {
      setDismissingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // Cancel a created/processing execution request
  const cancelRequest = async (id) => {
    try {
      setCancelingIds(prev => new Set(prev).add(id));
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/casc/v1/api/prompt-executions/${id}/cancel`, {
        method: 'POST',
        headers,
        body: '{}',
      });
      if (!resp.ok) {
        if (resp.status === 400) throw new Error('Request cannot be canceled or is already finished.');
        if (resp.status === 503) throw new Error('Service temporarily unavailable.');
        throw new Error(`Cancel failed (${resp.status}).`);
      }
      // Optimistically mark the item as canceled
      setItems(prev => prev.map(x => (x && x.id === id ? { ...x, status: 'canceled' } : x)));
      // Trigger a refresh to pick up any server-side changes
      if (typeof fetchRef.current === 'function') {
        fetchRef.current();
      }
    } catch (e) {
      console.error('[ExecutionQueue] Failed to cancel item', id, e);
      try { window.alert(`Failed to cancel: ${e.message || e}`); } catch {}
    } finally {
      setCancelingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // Hide entirely if no items and not loading
  if (!loading && (!items || items.length === 0)) return null;

  return (
    <div className="execution-queue eq-container">
      <button
        onClick={() => setCollapsed(s => !s)}
        className="eq-toggle"
        aria-expanded={!collapsed}
        aria-controls={`execution-queue-${pageKey}`}
      >
        <i className="fas fa-tasks eq-toggle-icon" aria-hidden="true"></i>
        <strong className="eq-title">Prompt executions</strong>
        <span className="eq-subtitle">
          ({activeCount} active{failedCount > 0 ? `, ${failedCount} need attention` : ''})
        </span>
        <span className="eq-chevron" aria-hidden="true">
          <i className={`fas fa-chevron-${collapsed ? 'down' : 'up'}`}></i>
        </span>
      </button>

      {!collapsed && (
        <div id={`execution-queue-${pageKey}`} className="eq-content">
          {loading && items.length === 0 ? (
            <div className="eq-loading">
              <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
              <span>Loading queue...</span>
            </div>
          ) : error ? (
            <div className="eq-error">
              <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
              <span>{error}</span>
            </div>
          ) : (
            <ul className="eq-list">
              {visibleItems.map((item) => {
                const name = promptNameById[item.prompt_id] || `Prompt ${item.prompt_id}`;
                const createdAt = item.created_at ? new Date(item.created_at) : null;
                const updatedAt = item.updated_at ? new Date(item.updated_at) : null;
                const hasEntries = !!item.entries && typeof item.entries === 'object';
                const statusCounts = hasEntries && item.entries.status_counts ? item.entries.status_counts : {};
                const metadata = item.metadata || {};
                const failed = requestHasFailure(item);
                const errorMessage = failed ? extractExplicitError(metadata) : null;
                const statsLine = formatPromotionStats(metadata);
                const reportEntries = formatExecutionReport(metadata.execution_report);
                return (
                  <li key={item.id} className={`eq-item${failed ? ' eq-item--failed' : ''}`}>
                    <div className="eq-item-header" title={`Request ID: ${item.id}`} aria-label={`Request ID: ${item.id}`}>
                      <div className="eq-item-left">
                        <div className="eq-item-name">{name}</div>
                        <button
                          className="eq-secondary-btn"
                          onClick={() => toggleExpand(item.id)}
                          title="View entries for this request"
                          aria-expanded={expandedRequestIds.has(item.id)}
                        >
                          <i className={`fas fa-${expandedRequestIds.has(item.id) ? 'minus' : 'plus'}`}></i>{' '}
                          {expandedRequestIds.has(item.id) ? 'Hide entries' : 'View entries'}
                        </button>
                        {(item.status === 'created' || item.status === 'processing') && (
                          <button
                            className="eq-cancel-btn"
                            onClick={() => cancelRequest(item.id)}
                            disabled={cancelingIds.has(item.id)}
                            title="Cancel this request"
                            aria-label="Cancel this request"
                          >
                            {cancelingIds.has(item.id) ? (
                              <>
                                <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Canceling...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-ban" aria-hidden="true"></i> Cancel
                              </>
                            )}
                          </button>
                        )}
                        {item.status !== 'created' && item.status !== 'processing' && !metadata.acknowledged && (
                          <button
                            className="eq-clear-btn"
                            onClick={() => clearItemFromQueue(item.id)}
                            disabled={dismissingIds.has(item.id)}
                            title="Clear this run from the queue"
                            aria-label="Clear this run from the queue"
                          >
                            {dismissingIds.has(item.id) ? (
                              <>
                                <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Clearing...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-times-circle" aria-hidden="true"></i> Clear
                              </>
                            )}
                          </button>
                        )}
                      </div>
                      <div className="eq-item-status">
                        <StatusBadge status={item.status} failed={failed} />
                      </div>
                    </div>
                    <div className="eq-item-body">
                      {item.status === 'processing' && hasEntries && item.entries.status_counts && (
                        <SegmentedProgress statusCounts={statusCounts} />
                      )}
                      <div className="eq-meta">
                        <RelativeTime value={createdAt} prefix="Created: " />
                        {item.status === 'processing' && (
                          <span>
                            Active for <DurationTimer start={createdAt} />
                          </span>
                        )}
                        {item.status === 'processing' && hasEntries && item.entries.status_counts ? (() => {
                          const sc = statusCounts || {};
                          const total = Object.values(sc).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0);
                          // Consider both successful processed and errored as completed toward ETA
                          let processed = 0;
                          let errored = 0;
                          for (const [k, v] of Object.entries(sc)) {
                            const key = normalizeForProgress(k);
                            const num = typeof v === 'number' ? v : 0;
                            if (key === 'processed') processed += num;
                            else if (key === 'errored') errored += num;
                          }
                          const completed = processed + errored;
                          const remaining = Math.max(0, total - completed);
                          const startMs = createdAt ? createdAt.getTime() : null;
                          const elapsed = startMs ? Math.max(0, nowMs - startMs) : 0;
                          if (completed >= 1 && remaining > 0 && elapsed > 0) {
                            const avgPerItem = elapsed / completed;
                            const etaRemainMs = Math.round(avgPerItem * remaining);
                            const finishAt = new Date(nowMs + etaRemainMs);
                            const title = `Estimated completion at ${finishAt.toLocaleString()}`;
                            return (
                              <span className="eq-meta-eta" title={title} aria-label={title}>
                                • ETA: ~{formatDuration(etaRemainMs)} remaining
                              </span>
                            );
                          }
                          return null;
                        })() : null}
                        <span className="eq-meta-updated">
                          <RelativeTime value={updatedAt} prefix="Updated: " />
                        </span>
                      </div>
                      {errorMessage ? (
                        <div className="eq-error-message eq-error-message--prominent" role="alert">
                          <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
                          <div className="eq-error-message-body">
                            <strong>Execution failed</strong>
                            <pre className="eq-error-message-text">{String(errorMessage)}</pre>
                            {metadata.guidance?.steps?.length ? (
                              <ul className="eq-error-guidance">
                                {metadata.guidance.steps.map((step, i) => (
                                  <li key={i}>{step}</li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        </div>
                      ) : failed ? (
                        <div className="eq-error-message eq-error-message--prominent" role="alert">
                          <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
                          <div className="eq-error-message-body">
                            <strong>Execution failed</strong>
                            <p className="eq-error-message-text">
                              One or more entries failed. Expand &quot;View entries&quot; for per-row errors.
                              {statusCounts ? ` (${Number(statusCounts.errored || 0)} errored, ${Number(statusCounts.processed || 0)} processed)` : ''}
                            </p>
                          </div>
                        </div>
                      ) : null}
                      {statsLine ? (
                        <div className="eq-info-message" title={JSON.stringify(metadata.stats)}>
                          <i className="fas fa-info-circle" aria-hidden="true"></i>
                          <span>{statsLine}</span>
                        </div>
                      ) : null}
                      {reportEntries ? (
                        <div className="eq-report" role="region" aria-label="Execution report">
                          <div className="eq-report-title">
                            <i className="fas fa-clipboard-list" aria-hidden="true"></i>
                            How the agent was called
                            {metadata.execution_report?.finished_at ? (
                              <span className="eq-report-time">
                                {' '}
                                <RelativeTime value={new Date(metadata.execution_report.finished_at)} prefix="" />
                              </span>
                            ) : null}
                          </div>
                          <p className="eq-report-hint">
                            Document delivery: <code>inlined_text</code> = PDF text was extracted and appended to the prompt;
                            <code>placeholder_or_binary</code> = model likely only saw the filename.
                          </p>
                          <table className="eq-report-table">
                            <thead>
                              <tr>
                                <th>Entry</th>
                                <th>Document</th>
                                <th>Outputs written</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reportEntries.map((row) => (
                                <tr key={row.entry_id || row.status}>
                                  <td className="eq-report-mono" title={row.entry_id}>{String(row.entry_id || '').slice(0, 8)}…</td>
                                  <td>
                                    {row.documentDelivery || '—'}
                                    {row.documentChars != null ? ` (${row.documentChars} chars)` : ''}
                                    {row.warning ? (
                                      <div className="eq-report-warn">{row.warning}</div>
                                    ) : null}
                                  </td>
                                  <td>
                                    {row.outputPreview && typeof row.outputPreview === 'object'
                                      ? Object.entries(row.outputPreview).map(([k, v]) => (
                                        <div key={k}><strong>{k}:</strong> {String(v)}</div>
                                      ))
                                      : (row.outputKeysWritten?.length
                                        ? row.outputKeysWritten.join(', ')
                                        : '—')}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                      {expandedRequestIds.has(item.id) ? (() => {
                        const st = entriesStateByRequestId[item.id] || getDefaultEntriesState();
                        return (
                          <div className="eq-entries-panel">
                            {st.loading && st.items.length === 0 ? (
                              <div className="eq-loading">
                                <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                                <span>Loading entries...</span>
                              </div>
                            ) : st.error ? (
                              <div className="eq-error">
                                <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
                                <span>{st.error}</span>
                              </div>
                            ) : st.items.length === 0 ? (
                              <div className="eq-empty">
                                No processing rows found for this request.
                                {failed ? ' The request may have failed before entries were created.' : ''}
                              </div>
                            ) : (
                              <div className="eq-entries-table-wrap">
                                <table className="eq-entries-table">
                                  <thead>
                                    <tr>
                                      <th>Row ID</th>
                                      <th>Entry ID</th>
                                      <th>Status</th>
                                      <th>Created</th>
                                      <th>Updated</th>
                                      <th>Started</th>
                                      <th>Document</th>
                                      <th>Outputs</th>
                                      <th>Error</th>
                                      <th>Data</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {st.items.map(en => {
                                      const created = en.created_at ? new Date(en.created_at) : null;
                                      const updated = en.updated_at ? new Date(en.updated_at) : null;
                                      const started = en.processing_started_at ? new Date(en.processing_started_at) : null;
                                      const entryKey = String(en.entry_id || en.id || '');
                                      const dataRow = (st.entryDataById && entryKey) ? st.entryDataById[entryKey] : null;
                                      const isExpanded = (st.expandedEntryIds || new Set()).has(entryKey);
                                      const pt = en.processing_trace && typeof en.processing_trace === 'object'
                                        ? en.processing_trace
                                        : null;
                                      const errMsg = (function extractEntryError(e) {
                                        if (!e) return null;
                                        if (typeof e.processing_error === 'string') return e.processing_error;
                                        if (typeof e.error === 'string') return e.error;
                                        if (typeof e.error_message === 'string') return e.error_message;
                                        if (typeof e.message === 'string') return e.message;
                                        if (e.metadata) {
                                          const m = e.metadata;
                                          if (typeof m === 'string') return m;
                                          if (typeof m.error === 'string') return m.error;
                                          if (m.error && typeof m.error.message === 'string') return m.error.message;
                                          if (typeof m.error_message === 'string') return m.error_message;
                                        }
                                        return null;
                                      })(en) || null;
                                      return (
                                        <>
                                          <tr key={(en.id || `${en.entry_id}-${en.created_at}`) + '-row'}>
                                            <td title={String(en.id || '')}>{String(en.id || '')}</td>
                                            <td title={String(en.entry_id || '')}>{String(en.entry_id || '')}</td>
                                            <td><StatusBadge status={en.status} /></td>
                                            <td><RelativeTime value={created} /></td>
                                            <td><RelativeTime value={updated} /></td>
                                            <td><RelativeTime value={started} /></td>
                                            <td className="eq-report-doc" title={pt?.warning || ''}>
                                              {pt
                                                ? `${pt.documentDelivery || '?'}${pt.documentChars != null ? ` (${pt.documentChars} chars)` : ''}`
                                                : '—'}
                                            </td>
                                            <td className="eq-report-out">
                                              {pt?.outputPreview && typeof pt.outputPreview === 'object'
                                                ? Object.entries(pt.outputPreview).map(([k, v]) => (
                                                  <div key={k}><strong>{k}:</strong> {String(v).slice(0, 120)}</div>
                                                ))
                                                : (pt?.outputKeysWritten?.length ? pt.outputKeysWritten.join(', ') : '—')}
                                            </td>
                                            <td className="eq-entry-error" title={errMsg || ''}>{errMsg ? String(errMsg) : ''}</td>
                                            <td>
                                              {st.entryDataLoading ? (
                                                <span title="Loading entry data..."><i className="fas fa-spinner fa-spin" aria-hidden="true"></i></span>
                                              ) : st.entryDataError ? (
                                                <span className="eq-error" title={st.entryDataError}>
                                                  <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
                                                  <button
                                                    className="eq-secondary-btn"
                                                    style={{ marginLeft: 6 }}
                                                    onClick={() => loadEntryDataFor(item.id, [entryKey])}
                                                  >Retry</button>
                                                </span>
                                              ) : dataRow ? (
                                                <button
                                                  className="eq-secondary-btn"
                                                  onClick={() => toggleEntryDetails(item.id, entryKey)}
                                                  aria-expanded={isExpanded}
                                                >{isExpanded ? 'Hide' : 'View'} data</button>
                                              ) : (
                                                <span className="eq-empty">No data</span>
                                              )}
                                            </td>
                                          </tr>
                                          {isExpanded && (
                                            <tr key={(en.id || `${en.entry_id}-${en.created_at}`) + '-details'}>
                                              <td colSpan={10}>
                                                {dataRow ? (
                                                  <div className="eq-entry-data">
                                                    <RawEntryFields
                                                      entry={dataRow}
                                                      fieldsClass="data-item-fields"
                                                      fieldClass="data-field"
                                                      outputsSectionClass="outputs-section"
                                                      fileDownloadContext={fileDownloadContext}
                                                    />
                                                  </div>
                                                ) : (
                                                  <div className="eq-empty">No data available for this entry.</div>
                                                )}
                                              </td>
                                            </tr>
                                          )}
                                        </>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                <div className="eq-entries-pagination">
                                  <button
                                    className="eq-secondary-btn"
                                    onClick={() => loadEntriesPage(item.id, Math.max(1, (st.page || 1) - 1), st.pageSize)}
                                    disabled={st.loading || (st.page || 1) <= 1}
                                  >
                                    <i className="fas fa-chevron-left"></i> Prev
                                  </button>
                                  <span className="eq-page-indicator">Page {(st.page || 1)}</span>
                                  <button
                                    className="eq-secondary-btn"
                                    onClick={() => loadEntriesPage(item.id, (st.page || 1) + 1, st.pageSize)}
                                    disabled={st.loading || !st.hasMore}
                                  >
                                    Next <i className="fas fa-chevron-right"></i>
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })() : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default ExecutionQueue;
